/**
 * webPush: "Notifications on this phone", the app's half (webpush).
 *
 * The switch subscribes this browser to Web Push with Karr's public VAPID key
 * and saves the subscription (`save_my_push_subscription`). From then on
 * send-push reaches the phone while Karr is closed; public/push-sw.js shows it.
 *
 * Rules of the road, as desktopNotify.ts keeps them:
 *   - Permission is asked only from a tap (browsers refuse it otherwise, and a
 *     prompt on page load is hostile anyway).
 *   - An iPhone only takes Web Push from Karr opened on the home screen
 *     (iOS 16.4+). In Safari the answer is "Add Karr to your Home Screen
 *     first", never a switch that cannot work.
 *   - A phone that signs out stops getting the last person's alerts.
 *
 * @since webpush
 */
import { supabase } from '@/db/supabase';
import { iosVersion, isIOSDevice, isStandalone } from '@/shared/pwa/installed';
import { PUSH_CHANGED_EVENT } from './pushAsk';

/** On this device, from the person's point of view. */
export type PushState =
    | 'unsupported'      // this browser has no Web Push
    | 'ios-update'       // an iPhone older than iOS 16.4: update it first (nozoom)
    | 'home-screen'      // an iPhone in Safari: add Karr to the Home Screen first
    | 'unconfigured'     // this build has no public key
    | 'denied'           // the phone blocks notifications for Karr
    | 'off'
    | 'on';

const ON_KEY = 'karr_push_on';
const VAPID_PUBLIC_KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined)?.trim() ?? '';

function hasPushApi(): boolean {
    return typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
}

function rememberOn(on: boolean): void {
    try { if (on) localStorage.setItem(ON_KEY, '1'); else localStorage.removeItem(ON_KEY); } catch { /* storage blocked */ }
    // webpushask: the popup, the rail row and the Settings switch follow each other.
    try { window.dispatchEvent(new Event(PUSH_CHANGED_EVENT)); } catch { /* no window */ }
}

/** True once this device said yes. Read synchronously by the bell's engine. */
export function pushOnHere(): boolean {
    try { return localStorage.getItem(ON_KEY) === '1' && Notification.permission === 'granted'; } catch { return false; }
}

/** A phone, as against a laptop: what the switch calls "this phone". */
export function isPhone(): boolean {
    if (typeof navigator === 'undefined') return false;
    return isIOSDevice() || /Android|Mobile/i.test(navigator.userAgent);
}

/** What the device can do before asking it anything. */
export function pushCapability(): Exclude<PushState, 'denied' | 'off' | 'on'> | 'ok' {
    // Before 16.4 an iPhone has no Web Push at all: adding Karr to the Home
    // Screen would not help, updating the iPhone does.
    const v = iosVersion();
    if (v && (v[0] < 16 || (v[0] === 16 && v[1] < 4))) return 'ios-update';
    if (isIOSDevice() && !isStandalone()) return 'home-screen';
    if (!hasPushApi()) return 'unsupported';
    if (!VAPID_PUBLIC_KEY) return 'unconfigured';
    return 'ok';
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
    try {
        const existing = await navigator.serviceWorker.getRegistration();
        if (existing) return existing;
        // Registered on load by swUpdate.ts; wait for it, but not forever.
        return await Promise.race([
            navigator.serviceWorker.ready,
            new Promise<null>((r) => setTimeout(() => r(null), 8000)),
        ]);
    } catch {
        return null;
    }
}

/** The switch's state on this device. */
export async function pushState(): Promise<PushState> {
    const cap = pushCapability();
    if (cap !== 'ok') return cap;
    if (Notification.permission === 'denied') return 'denied';
    if (Notification.permission !== 'granted') return 'off';
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription().catch(() => null);
    return sub && pushOnHere() ? 'on' : 'off';
}

/** base64url → the bytes `applicationServerKey` takes. */
export function keyBytes(b64url: string): Uint8Array {
    const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
    const b64 = (b64url + pad).replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

async function save(sub: PushSubscription): Promise<string | null> {
    const json = sub.toJSON();
    const { error } = await supabase.rpc('save_my_push_subscription', {
        p_endpoint: sub.endpoint,
        p_p256dh: json.keys?.p256dh ?? '',
        p_auth: json.keys?.auth ?? '',
        p_user_agent: navigator.userAgent.slice(0, 512),
    });
    if (!error) return null;
    if (/save_my_push_subscription|does not exist|schema cache/i.test(error.message ?? '')) {
        return 'Karr cannot send to phones yet. This is being switched on at our end, try again later today.';
    }
    return 'Karr could not save this phone. Check your connection and tap again.';
}

/**
 * Turn it on. Call ONLY from a tap: it may ask the phone for permission.
 * Returns the state it reached, and what went wrong when it stopped short.
 */
export async function turnOnPush(): Promise<{ state: PushState; error?: string }> {
    const cap = pushCapability();
    if (cap !== 'ok') return { state: cap };
    let perm = Notification.permission;
    if (perm === 'default') {
        try { perm = await Notification.requestPermission(); } catch { perm = 'default'; }
    }
    if (perm === 'denied') return { state: 'denied' };
    if (perm !== 'granted') return { state: 'off' };

    const reg = await registration();
    if (!reg) return { state: 'off', error: 'Karr could not start its background helper on this phone. Close Karr, open it again, and tap once more.' };
    let sub: PushSubscription | null = null;
    try {
        sub = await reg.pushManager.getSubscription()
            ?? await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: keyBytes(VAPID_PUBLIC_KEY) as BufferSource,
            });
    } catch {
        return { state: 'off', error: 'This phone would not sign Karr up for notifications. Close Karr, open it again, and tap once more.' };
    }
    const err = await save(sub);
    if (err) return { state: 'off', error: err };
    rememberOn(true);
    return { state: 'on' };
}

/** Turn it off on this device: the phone forgets Karr, Karr forgets the phone. */
export async function turnOffPush(): Promise<PushState> {
    rememberOn(false);
    if (!hasPushApi()) return pushCapability() === 'ok' ? 'off' : pushCapability() as PushState;
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription().catch(() => null);
    if (sub) {
        try { await supabase.rpc('remove_my_push_subscription', { p_endpoint: sub.endpoint }); } catch { /* the push service will answer 410 */ }
        try { await sub.unsubscribe(); } catch { /* already gone */ }
    }
    return 'off';
}

/**
 * On each start: a device that said yes keeps its row current (the push
 * service may have rotated the endpoint, or someone else signed in here since
 * and saved it to themselves). Never asks for permission.
 */
export async function syncPushSubscription(): Promise<void> {
    if (!pushOnHere() || pushCapability() !== 'ok') return;
    const reg = await registration();
    if (!reg) return;
    try {
        const sub = await reg.pushManager.getSubscription()
            ?? await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: keyBytes(VAPID_PUBLIC_KEY) as BufferSource,
            });
        await save(sub);
    } catch { /* tried; the switch shows the truth */ }
}

/** Signing out of this phone stops its alerts. Runs BEFORE the session ends. */
export async function forgetPushOnSignOut(): Promise<void> {
    if (!pushOnHere()) return;
    await Promise.race([turnOffPush(), new Promise((r) => setTimeout(r, 3000))]);
}

/** "Send a test": one alert for me, shown even while Karr is open. */
export async function sendTestPush(orgId: string): Promise<{ ok: boolean; message: string }> {
    const { data, error } = await supabase.rpc('send_me_a_test_push', { p_org_id: orgId });
    if (error) {
        return /send_me_a_test_push|does not exist|schema cache/i.test(error.message ?? '')
            ? { ok: false, message: 'Karr cannot send to phones yet. This is being switched on at our end, try again later today.' }
            : { ok: false, message: 'The test did not go. Check your connection and tap again.' };
    }
    if (Number(data) === 0) {
        return { ok: false, message: 'Karr has no phone saved for you. Turn the switch off and on again.' };
    }
    return { ok: true, message: 'Sent. It should arrive within a few seconds.' };
}

/** The red number on the home-screen icon. No-op where the phone has none. */
export function setIconCount(n: number): void {
    try {
        const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
        if (n > 0) void nav.setAppBadge?.(n)?.catch(() => {});
        else void nav.clearAppBadge?.()?.catch(() => {});
    } catch { /* no badge API */ }
}
